import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useForm } from 'react-final-form';
import { makeStyles } from 'styles/util';
import { getStorageGroupTitle, getStorageTitle } from 'helpers/StorageSiteHelper';
import { formatIsoDate } from 'helpers/DateHelper';
import rentalTypes from 'enums/rentalTypes';
import bookingStatuses from 'enums/bookingStatuses';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { TextField } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import { OnChange } from 'react-final-form-listeners';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

const useStyles = makeStyles(({ colors }) => ({
    notAvailable: {
        color: colors.textColorDisabled
    }
}));

const StorageSelectorDialog = ({ storageSite, open, onOk, onCancel }) => {
    const classes = useStyles();
    const form = useForm();
    const formValues = form.getState().values;
    const { appContext } = useAppContext();

    useEffect(() => {
        if(open) {
            form.mutators.changeValue({ name: 'storageSelectorDialog.storageGroupId', value: formValues.storageGroupId });
            form.mutators.changeValue({ name: 'storageSelectorDialog.storageId', value: formValues.storageId ?? 0 });
        }
    }, [open]);

    const selectedStorageGroup = storageSite.storageGroups
        .find(storageGroup => storageGroup.id === formValues.storageSelectorDialog.storageGroupId);

    const storageGroupListItems = storageSite.storageGroups
        .filter(storageGroup => storageGroup.rentalType !== rentalTypes.customArea.key)
        .map(storageGroup => ({
            value: storageGroup.id,
            title: getStorageGroupTitle(storageGroup, appContext, { useSpecialCustomAreaLabel: true })
        }));

    const availableStorageIds = storageSite.bookingCalendar.storageGroupBookingCalendars
        .find(sgbc => sgbc.storageGroupId === selectedStorageGroup?.id)
        ?.storageBookingCalendars.filter(sbc => sbc.availableBookingIntervals.length === 1 && sbc.availableBookingIntervals[0].startDate === formatIsoDate(new Date(), appContext))
        .map(sbc => sbc.storageId);

    const storageListItems = [{ value: 0, title: '--- select ---' }]
        .concat((selectedStorageGroup?.storages ?? [])
            .map(storage => ({
                value: storage.id,
                title: getStorageTitle(storage, selectedStorageGroup, appContext, { showAreaWhenTitleExists: false }),
                isAvailable: availableStorageIds.includes(storage.id)
            }))
        );

    const handleStorageGroupChange = () => {
        form.mutators.changeValue({ name: 'storageSelectorDialog.storageId', value: 0 });
    };

    const getStorageIdRequired = () => formValues.bookingStatus !== bookingStatuses.requested.key;

    const isValid = () => !getStorageIdRequired() || formValues.storageSelectorDialog.storageId > 0;

    const handleOk = () => {
        onOk({
            storageGroupId: formValues.storageSelectorDialog.storageGroupId,
            storageId: formValues.storageSelectorDialog.storageId === 0
                ? undefined
                : formValues.storageSelectorDialog.storageId
        });
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onCancel}>
            <DialogTitle disableTypography>
                <Typography variant="h5">
                    Select storage
                </Typography>
            </DialogTitle>
            <DialogContent>
                <TextField
                    select
                    name="storageSelectorDialog.storageGroupId"
                    label="Storage group"
                    variant="outlined"
                >
                    {
                        storageGroupListItems.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.title}</MenuItem>
                        ))
                    }
                </TextField>
                <OnChange name="storageSelectorDialog.storageGroupId">
                    {handleStorageGroupChange}
                </OnChange>

                <TextField
                    select
                    name="storageSelectorDialog.storageId"
                    label="Storage"
                    variant="outlined"
                >
                    {
                        storageListItems.map(o => (
                            <MenuItem key={o.value} value={o.value}>
                                <Box component="span" className={o.isAvailable ? '' : classes.notAvailable}>
                                    {o.title}
                                    {!o.isAvailable && o.value !== 0 && ' - not available'}
                                </Box>
                            </MenuItem>
                        ))
                    }
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid()}
                    onClick={handleOk}
                >
                    OK
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>

    );
};

StorageSelectorDialog.propTypes = {
    storageSite: PropTypes.object.isRequired,
    open: PropTypes.bool,
    onOk: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default StorageSelectorDialog;
